import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchFeedbackStart, fetchFeedbackSuccess, fetchFeedbackFailure } from "../../../../Redux/slices/jcSlice";
import { getFeedbackApi } from "../../../../services/jcReview/feedbackApi";
import {
  Dialog, DialogContent, Box, Typography, LinearProgress, IconButton, Chip, Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import Loader from "@features/shared/components/Loader";
import Message from "@features/shared/components/Message";

const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];

const ratingColor = (r) => r <= 4 ? "#e60000" : r <= 6 ? "#FF9500" : "#34C759";
const ratingBg = (r) => r <= 4 ? "#fff0f0" : r <= 6 ? "#FFF8F0" : "#f0fff4";
const statusLabel = (r) => r <= 4 ? "Needs Improvement" : r <= 6 ? "Satisfactory" : "Good";

const BMFeedbackView = ({ submission, isOpen, onClose, title = "ZM Feedback" }) => {
  const dispatch = useDispatch();
  const { feedback, feedbackLoading } = useSelector((s) => s.jc);

  useEffect(() => {
    if (!isOpen || !submission) return;
    dispatch(fetchFeedbackStart());
    getFeedbackApi(submission.id)
      .then((res) => dispatch(fetchFeedbackSuccess(res.data)))
      .catch((err) => dispatch(fetchFeedbackFailure(err?.response?.data?.msg || "Failed to load feedback")));
  }, [isOpen, submission, dispatch]);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: { xs: 3, sm: 4 },
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
          mx: { xs: 1, sm: "auto" },
          my: { xs: 2, sm: "auto" },
        },
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #3901ff 0%, #6a3de8 100%)",
          px: { xs: 2, sm: 3 }, py: { xs: 1.75, sm: 2 },
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: { xs: "1rem", sm: "1.15rem" }, color: "#fff", lineHeight: 1.3 }}>
            {title}
          </Typography>
          {submission && (
            <Typography sx={{ fontSize: { xs: "0.78rem", sm: "0.82rem" }, color: "rgba(255,255,255,0.72)", mt: 0.3 }}>
              {MONTH_NAMES[submission.month - 1]} {submission.year}
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: "rgba(255,255,255,0.85)" }}>
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        {feedbackLoading ? (
          <Box sx={{ py: 4 }}><Loader loading /></Box>
        ) : feedback.length === 0 ? (
          <Box sx={{ py: 4 }}><Message message="No feedback available yet." /></Box>
        ) : (
          <Box>
            {/* ── Score legend ── */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 1,
                px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 2.5 },
              }}
            >
              {[
                { range: "1 – 4", label: "Needs Improvement", color: "#e60000", bg: "#fff0f0" },
                { range: "5 – 6", label: "Satisfactory", color: "#FF9500", bg: "#FFF8F0" },
                { range: "7 – 10", label: "Good / Excellent", color: "#34C759", bg: "#f0fff4" },
              ].map((band) => (
                <Box
                  key={band.range}
                  sx={{
                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                    textAlign: "center", gap: 0.3,
                    px: 1, py: 0.75, borderRadius: 2,
                    backgroundColor: band.bg, border: `1.5px solid ${band.color}25`,
                  }}
                >
                  <Typography sx={{ fontSize: { xs: "0.82rem", sm: "0.88rem" }, fontWeight: 800, color: band.color, lineHeight: 1 }}>
                    {band.range}
                  </Typography>
                  <Typography sx={{ fontSize: { xs: "0.62rem", sm: "0.68rem" }, color: band.color, opacity: 0.85, lineHeight: 1.2 }}>
                    {band.label}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* ── Feedback entries ── */}
            <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2, pb: 3, display: "flex", flexDirection: "column", gap: 0 }}>
              {feedback.map((fb, idx) => {
                const color = ratingColor(fb.rating);
                const bg = ratingBg(fb.rating);
                return (
                  <Box key={fb.id}>
                    {idx > 0 && <Divider sx={{ my: 2.5 }} />}

                    {/* Reviewer row */}
                    <Box
                      sx={{
                        display: "flex", alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1, mb: 1.5,
                        p: { xs: 1.25, sm: 1.5 }, borderRadius: 2,
                        backgroundColor: "#f5f5f7",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                        <Box sx={{
                          width: { xs: 32, sm: 36 }, height: { xs: 32, sm: 36 },
                          borderRadius: "50%", flexShrink: 0,
                          backgroundColor: "#ede9ff",
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                          <PersonOutlineIcon sx={{ fontSize: { xs: 17, sm: 19 }, color: "#3901ff" }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 600, fontSize: { xs: "0.85rem", sm: "0.9rem" }, color: "#111", lineHeight: 1.25, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {fb.reviewer_name}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, flexShrink: 0 }}>
                        <CalendarTodayOutlinedIcon sx={{ fontSize: 12, color: "#bbb" }} />
                        <Typography sx={{ fontSize: { xs: "0.68rem", sm: "0.72rem" }, color: "#aaa", whiteSpace: "nowrap" }}>
                          {new Date(fb.feedback_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Score card */}
                    <Box
                      sx={{
                        borderRadius: 2.5,
                        border: `1.5px solid ${color}25`,
                        backgroundColor: bg,
                        px: { xs: 1.75, sm: 2.25 }, py: { xs: 1.25, sm: 1.75 },
                        mb: 1.5,
                      }}
                    >
                      {/* Header row: label + status chip */}
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                        <Typography sx={{ fontSize: { xs: "0.72rem", sm: "0.76rem" }, fontWeight: 700, color: "#666", textTransform: "uppercase", letterSpacing: 0.8 }}>
                          Score Awarded
                        </Typography>
                        <Chip
                          icon={<StarRoundedIcon sx={{ fontSize: "13px !important", color: `${color} !important` }} />}
                          label={statusLabel(fb.rating)}
                          size="small"
                          sx={{
                            height: 22, backgroundColor: "#fff",
                            border: `1px solid ${color}35`, color,
                            fontWeight: 700, fontSize: { xs: "0.66rem", sm: "0.7rem" }, borderRadius: 2,
                            "& .MuiChip-label": { px: 0.75 },
                          }}
                        />
                      </Box>
                      {/* Score + progress bar */}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.3, flexShrink: 0 }}>
                          <Typography sx={{ fontSize: { xs: "2rem", sm: "2.4rem" }, fontWeight: 900, color, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
                            {fb.rating}
                          </Typography>
                          <Typography sx={{ fontSize: { xs: "0.78rem", sm: "0.82rem" }, color: "#bbb", fontWeight: 500 }}>/10</Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={fb.rating * 10}
                          sx={{
                            flex: 1, height: { xs: 7, sm: 9 }, borderRadius: 4,
                            backgroundColor: `${color}18`,
                            "& .MuiLinearProgress-bar": { backgroundColor: color, borderRadius: 4 },
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Remarks */}
                    {fb.remarks ? (
                      <Box>
                        <Typography sx={{ fontSize: { xs: "0.68rem", sm: "0.72rem" }, color: "#aaa", textTransform: "uppercase", letterSpacing: 0.8, mb: 0.75, fontWeight: 700 }}>
                          Reviewer's Remarks
                        </Typography>
                        <Box sx={{
                          borderRadius: 2, px: { xs: 1.25, sm: 1.75 }, py: { xs: 1, sm: 1.5 },
                          backgroundColor: "#f7f7f7",
                          borderLeft: `3px solid ${color}`,
                        }}>
                          <Typography sx={{ fontSize: { xs: "0.85rem", sm: "0.9rem" }, color: "#333", lineHeight: 1.65 }}>
                            {fb.remarks}
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Typography sx={{ fontSize: "0.78rem", color: "#bbb", fontStyle: "italic", pl: 0.5 }}>
                        No remarks provided by the reviewer.
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BMFeedbackView;
